'use client';

import { c as _c } from "react/compiler-runtime";
import { ApiError } from '@lib/api/api-response';
import { parseApiErrorResponse } from '@lib/api/utils/parse-api-error-response';
import { invalidateDealEntitlementQueries } from '@lib/billing/hooks/invalidate-deal-entitlement-queries';
import { useMutation, useQueryClient } from '@lib/query/client';
import { spaceTreeQueryKeys } from '@lib/space-tree/constants/space-tree-query-keys';
import { teamTemplatesQueryKey } from '@lib/team-templates/hooks/use-team-templates-query';
import type { CreateDealParams } from '../types/create-deal-params';
import type { Deal } from '../types/deal';
import { dealQueryKeys } from './deal-query-keys';
import { myDealsQueryKeys } from './my-deal-query-keys';

/**
 * Hook for creating a deal.
 *
 * @returns TanStack Query mutation with automatic cache invalidation
 */
export function useCreateDeal() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: dealQueryKeys.all
        });
        invalidateDealEntitlementQueries(queryClient);
        queryClient.invalidateQueries({
          queryKey: myDealsQueryKeys.all
        });
        if (variables.isTemplate) {
          queryClient.invalidateQueries({
            queryKey: spaceTreeQueryKeys.all
          });
          queryClient.invalidateQueries({
            queryKey: teamTemplatesQueryKey
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
async function _temp(params) {
  const response = await fetch("/api/deals", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(params)
  });
  if (!response.ok) {
    const {
      reason,
      body
    } = await parseApiErrorResponse(response);
    throw new ApiError(reason || "Failed to create deal", response.status, body);
  }
  return response.json();
}